import React from 'react';
import dayjs from 'dayjs';
import { Popconfirm, Row, Tooltip } from 'antd';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Eye } from 'iconsax-react';
import { PiPencil } from 'react-icons/pi';
import { RiErrorWarningLine } from 'react-icons/ri';
import { IoEarth } from 'react-icons/io5';
import { Delete } from 'react-iconly';
import isEmpty from 'lodash/isEmpty';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import { EMPTY_IMAGE_PLACEHOLDER } from '../../../../../const/system';
import urlPageDynamicNewsDetails from '../../../../../urls/urlPageDynamicNewsDetails';
import { Permissions } from '../../../../../const/permissions';
import getDictionaryIdFromDynamicData from '../../../../../lib/getDictionaryIdFromDynamicData';
import { PrivateComponent } from '../../../../../components/HasRights/HasRights';
import PublishedTag from '../../../../../components/PublishedTag';
import CollapsibleText from '../../../../../components/CollapsibleText';
import { renderDatetimeColumnWithUtcZeroTimeBool } from '../../../../../lib/renderDatetimeColumnWithUtcZeroTimeBool';



export const NEWS_DICT_STRUCTURE = 'news';

export const getNewsTableColumns = (
  handlePreviewDictionary,
  handleEdit,
  handleDelete,
  handleImageCardClick,
  tableSize,
  canViewTemplateColumn,
) => [
  {
    title: <IntlMessages id="ui-general-id" />,
    dataIndex: 'id',
    align: 'center',
    width: tableSize.width * 0.08,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-preview" />,
    dataIndex: 'preview',
    align: 'center',
    width: tableSize.width * 0.14,
    render: (value, item) => (
      <LazyLoadImage
        onClick={() => {
          if (!isEmpty(item?.preview)) {
            handleImageCardClick(item.preview);
          }
        }}
        style={!isEmpty(item?.preview) ? { cursor: 'pointer' } : {}}
        className='hp-cursor-pointer'
        effect='blur'
        key={item?.id}
        alt="news preview"
        placeholderSrc={EMPTY_IMAGE_PLACEHOLDER}
        src={value ? `${value}?time=${Date.now()}` : EMPTY_IMAGE_PLACEHOLDER}
      />
    ),
  },
  {
    title: <IntlMessages id="ui-general-title" />,
    dataIndex: 'title',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.16,
    render: (item, value) => {
      return (
        <div>
          <CollapsibleText text={item} maxLength={200} />
              &nbsp;
          {value?.is_published && dayjs(value.updated_at).isAfter(dayjs(value.published_at)) && (
            <Tooltip placement="right" title={<IntlMessages id="ui-general-unpublished-tooltip-title" />}>
                  &nbsp;
              <ExclamationCircleOutlined className="hp-text-color-warning-1" />
            </Tooltip>
          )}
        </div>
      );
    },
  },
  ...canViewTemplateColumn ? [ {
    title: <IntlMessages id="ui-general-template" />,
    align: 'center',
    dataIndex: 'template',
    sorter: true,
    width: tableSize.width * 0.15,
    render: (template) => template?.data?.title,
  },
  ] : [],
  {
    title: <IntlMessages id="ui-general-published" />,
    dataIndex: 'is_published',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.11,
    render: (value, item) => <PublishedTag published={Boolean(value)} publishedDate={item.published_at} />,
  },
  {
    title: <IntlMessages id="ui-general-released-at" />,
    dataIndex: 'released_at',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.115,
    render: (value, item) => renderDatetimeColumnWithUtcZeroTimeBool(value, item),
  },
  {
    title: <IntlMessages id="ui-general-released-to" />,
    dataIndex: 'released_to',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.115,
    render: (value, item) => renderDatetimeColumnWithUtcZeroTimeBool(value, item),
  },
  {
    title: <IntlMessages id="ui-general-action" />,
    key: 'action',
    align: 'center',
    width: tableSize.width * 0.13,
    render: (item) => {
      const dictionaryId = getDictionaryIdFromDynamicData(item?.dynamic_data?.data);

      return (
        <Row className="da-h-100" align="middle" justify="center">
          <PrivateComponent allowedPermissions={[ Permissions.CONTENT.NEWS.FIND ]}>
            <Tooltip placement="top" title={<IntlMessages id="ui-general-view" />}>
              <div className="hp-text-right">
                <Link
                  to={urlPageDynamicNewsDetails({ newsId: item.id })}
                  target="_blank"
                >
                  <Eye
                    size={20}
                    data-testid="view-icon"
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                  />
                </Link>
              </div>
            </Tooltip>
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.CONTENT.NEWS.EDIT ]}>
            <Tooltip placement="top" title={<IntlMessages id="ui-general-edit" />}>
              <div className="hp-text-right">
                <PiPencil
                  onClick={() => handleEdit(item.id)}
                  size={20}
                  className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                />
              </div>
            </Tooltip>
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.CONTENT.NEWS.DELETE ]}>
            {!item.is_published ? (
              <Popconfirm
                title={<IntlMessages id="ui-general-delete-confirm-message" />}
                placement="topLeft"
                onConfirm={() => handleDelete(item.id)}
                okText={<IntlMessages id="ui-general-yes" />}
                cancelText={<IntlMessages id="ui-general-no" />}
                icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                okButtonProps={{ danger: true }}
              >
                <Tooltip placement="top" title={<IntlMessages id="ui-general-delete" />}>
                  <div className="hp-text-right">
                    <Delete
                      size={20}
                      data-testid="delete-icon"
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              </Popconfirm>
            ) : (
              <Tooltip placement="top" title={<IntlMessages id="ui-general-unable-delete-published-item-notification" />}>
                <div className="hp-text-right">
                  <Delete
                    disabled
                    size={20}
                    data-testid="delete-icon"
                    className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW ]}>
            {dictionaryId && (
              <Tooltip placement="top" title={<IntlMessages id="ui-general-dictionary" />}>
                <div className="hp-text-right">
                  <IoEarth
                    size={20}
                    data-testid="dictionary-icon"
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                    onClick={() => handlePreviewDictionary(dictionaryId)}
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
        </Row>
      );
    },
  },
];
